import type { IconType } from "react-icons";
import {
  HiOutlineBookOpen,
  HiOutlineBriefcase,
  HiOutlineMusicalNote,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import type { AppUser } from "@/lib/types/next-auth";
import { UserRoles } from "@/lib/types/user-roles";
import { hasAnyAdminRoleFromFlags } from "./roles-common";

/**
 * Admin roles that can access admin dashboard
 */
export const ADMIN_ROLES = [
  UserRoles.Admin,
  UserRoles.Teacher,
  UserRoles.Staff,
  UserRoles.Choreographer,
] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/**
 * Get list of admin roles for a user
 */
export function getAdminRoles(user: AppUser | null | undefined): string[] {
  if (!user) return [];

  const adminRoles: string[] = [];
  if (user.isAdmin) adminRoles.push(UserRoles.Admin);
  if (user.isTeacher) adminRoles.push(UserRoles.Teacher);
  if (user.isStaff) adminRoles.push(UserRoles.Staff);
  if (user.isChoreographer) adminRoles.push(UserRoles.Choreographer);

  return adminRoles;
}

export function hasMultipleAdminRoles(
  user: AppUser | null | undefined,
): boolean {
  return getAdminRoles(user).length > 1;
}

/**
 * Whether the user has at least one of the admin/teacher/staff roles (capability check).
 * Use for: access gates, feature flags, "can this user access admin area at all".
 * For "what can they do right now" use getActiveRole() instead.
 */
export function hasAnyAdminRole(user: AppUser | null | undefined): boolean {
  return hasAnyAdminRoleFromFlags(
    user
      ? {
          isAdmin: user.isAdmin,
          isTeacher: user.isTeacher,
          isStaff: user.isStaff,
          isChoreographer: user.isChoreographer,
        }
      : null,
  );
}

/**
 * Current active role for UI/permissions (respects role switch).
 * Use this instead of checking user?.isAdmin / user?.isTeacher / user?.isStaff
 * when deciding what to show or allow (e.g. admin view vs teacher view).
 */
export function getActiveRole(user: AppUser | null | undefined): string | null {
  if (!user) return null;
  if (user.activeRole != null && user.activeRole !== "") return user.activeRole;
  if (user.isAdmin) return UserRoles.Admin;
  if (user.isTeacher) return UserRoles.Teacher;
  if (user.isStaff) return UserRoles.Staff;
  if (user.isChoreographer) return UserRoles.Choreographer;
  return null;
}

/**
 * Roles offered on the switcher. Choreographer stays hidden while the portal is off.
 * While the setting is still loading, the role stays so the menu does not flicker.
 */
export function getSwitchableAdminRoles(
  user: AppUser | null | undefined,
  options: {
    choreographerPortalEnabled: boolean;
    choreographerPortalKnown: boolean;
  },
): string[] {
  return getAdminRoles(user).filter((role) => {
    if (role !== UserRoles.Choreographer) return true;
    if (!options.choreographerPortalKnown) return true;
    return options.choreographerPortalEnabled;
  });
}

/**
 * Active role for menus and API calls. A saved Choreographer role falls back
 * to another role the person has while the portal is turned off.
 */
export function resolveVisibleActiveRole(
  user: AppUser | null | undefined,
  choreographerPortalEnabled: boolean,
  choreographerPortalKnown: boolean,
): string | null {
  const active = getActiveRole(user);
  if (active !== UserRoles.Choreographer) return active;
  if (!choreographerPortalKnown || choreographerPortalEnabled) return active;
  if (user?.isAdmin) return UserRoles.Admin;
  if (user?.isTeacher) return UserRoles.Teacher;
  if (user?.isStaff) return UserRoles.Staff;
  return active;
}

/**
 * Role label for the dashboard top-bar badge.
 * Only the studio `/admin` shell shows Admin/Teacher/Staff.
 * `/portal` is always the parent shell, even when the same account
 * also has those roles.
 */
export function getDashboardRoleBadge(
  pathname: string | null | undefined,
  user: AppUser | null | undefined,
): string | null {
  if (!user) return null;
  if (!pathname?.startsWith("/admin")) return null;
  return getActiveRole(user);
}

/**
 * Role sent with API calls. `/portal` sends none, matching a parent-only account.
 * A shared email that is also Admin must not tag parent-portal requests as Admin.
 */
export function resolveActiveRoleApiHeader(
  pathname: string | null | undefined,
  user: AppUser | null | undefined,
): string | null {
  if (pathname?.startsWith("/portal")) return null;
  return getActiveRole(user);
}

/** Studio admin actions (refund, void). Never on the parent portal. */
export function canActAsStudioAdmin(
  pathname: string | null | undefined,
  user: AppUser | null | undefined,
): boolean {
  return resolveActiveRoleApiHeader(pathname, user) === UserRoles.Admin;
}

export function getRoleDisplayName(role: string): string {
  return role;
}

export function getRoleIcon(role: string): IconType {
  switch (role) {
    case UserRoles.Admin:
      return HiOutlineShieldCheck;
    case UserRoles.Teacher:
      return HiOutlineBookOpen;
    case UserRoles.Staff:
      return HiOutlineBriefcase;
    case UserRoles.Choreographer:
      return HiOutlineMusicalNote;
    default:
      return HiOutlineShieldCheck;
  }
}

export function getRoleDescription(role: string): string {
  switch (role) {
    case UserRoles.Admin:
      return "Full access to manage classes, students, teachers, and appointments.";
    case UserRoles.Teacher:
      return "View your classes, private lessons, and hours.";
    case UserRoles.Staff:
      return "Support parents, check private lessons, and manage your time.";
    case UserRoles.Choreographer:
      return "Your dances, contract, and deliveries.";
    default:
      return "";
  }
}

export function getRoleColorScheme(role: string): string {
  switch (role) {
    case UserRoles.Admin:
      return "purple";
    case UserRoles.Teacher:
      return "blue";
    case UserRoles.Staff:
      return "green";
    case UserRoles.Choreographer:
      return "orange";
    default:
      return "gray";
  }
}

export interface RoleAccentStyles {
  wellBg: string;
  iconColor: string;
  borderHover: string;
}

export function getRoleAccentStyles(
  role: string,
  isDark: boolean,
): RoleAccentStyles {
  switch (role) {
    case UserRoles.Admin:
      return {
        wellBg: isDark ? "rgba(168, 85, 247, 0.16)" : "rgba(126, 34, 206, 0.1)",
        iconColor: isDark ? "#c084fc" : "#7e22ce",
        borderHover: isDark
          ? "rgba(192, 132, 252, 0.45)"
          : "rgba(126, 34, 206, 0.35)",
      };
    case UserRoles.Teacher:
      return {
        wellBg: isDark ? "rgba(59, 130, 246, 0.16)" : "rgba(37, 99, 235, 0.1)",
        iconColor: isDark ? "#60a5fa" : "#1d4ed8",
        borderHover: isDark
          ? "rgba(96, 165, 250, 0.45)"
          : "rgba(37, 99, 235, 0.35)",
      };
    case UserRoles.Staff:
      return {
        wellBg: isDark ? "rgba(34, 197, 94, 0.16)" : "rgba(22, 163, 74, 0.1)",
        iconColor: isDark ? "#4ade80" : "#15803d",
        borderHover: isDark
          ? "rgba(74, 222, 128, 0.45)"
          : "rgba(22, 163, 74, 0.35)",
      };
    case UserRoles.Choreographer:
      return {
        wellBg: isDark ? "rgba(249, 115, 22, 0.16)" : "rgba(234, 88, 12, 0.1)",
        iconColor: isDark ? "#fb923c" : "#c2410c",
        borderHover: isDark
          ? "rgba(251, 146, 60, 0.45)"
          : "rgba(234, 88, 12, 0.35)",
      };
    default:
      return {
        wellBg: isDark
          ? "rgba(148, 163, 184, 0.14)"
          : "rgba(100, 116, 139, 0.1)",
        iconColor: isDark ? "#94a3b8" : "#475569",
        borderHover: isDark
          ? "rgba(148, 163, 184, 0.4)"
          : "rgba(100, 116, 139, 0.3)",
      };
  }
}

export function isValidAdminRole(
  role: string | null | undefined,
): role is AdminRole {
  if (!role) return false;
  return ADMIN_ROLES.includes(role as AdminRole);
}
